import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { useSEO } from "@/lib/useSEO";
import { HEARD_OPTIONS } from "@/lib/heardOptions";
import { Button } from "@/components/ui/button";
import { Monitor, GoogleLogo, ShieldCheck, Lightning, EnvelopeSimple, CircleNotch, CheckCircle } from "@phosphor-icons/react";

const LOGIN_BG = "https://images.unsplash.com/photo-1709625862266-014ef072fd93?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjB0ZWNofGVufDB8fHx8MTc4Mzk5NjM3NXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [heard, setHeard] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  useSEO({
    title: "Sign in to Controlly \u2014 Remote Desktop from Your Browser",
    description:
      "Log in or create your free Controlly account to control your computers remotely from any browser. Google sign-in or passwordless email link.",
    path: "/login",
  });

  useEffect(() => {
    if (!loading && user) {
      let dest = "/dashboard";
      try {
        const next = localStorage.getItem("controlly_next");
        if (next && next.startsWith("/") && !next.startsWith("//")) dest = next;
        localStorage.removeItem("controlly_next");
      } catch { /* ignore */ }
      navigate(dest, { replace: true });
    }
  }, [user, loading, navigate]);

  const handleLogin = () => {
    if (!agreed) return;
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!agreed || !name.trim() || !email.trim() || !heard) return;
    setSending(true);
    setErr("");
    try {
      const res = await api.post("/auth/magic-link/request", {
        name: name.trim(), email: email.trim(), heard_about_us: heard,
      });
      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-black">
      <img src={LOGIN_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at center, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.95) 75%)" }} />

      <div className="relative z-10 w-full max-w-md px-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <a href="/" data-testid="home-logo-link" aria-label="Go to home" className="flex items-center gap-3 mb-8 group w-fit">
          <img src="/controlly-icon.png" alt="Controlly" className="w-11 h-11 rounded-md drop-shadow-[0_2px_10px_rgba(37,120,255,0.45)] group-hover:scale-105 transition-transform" />
          <div>
            <div className="font-heading text-lg font-extrabold tracking-tight leading-none">Controlly</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Remote Desktop Control</div>
          </div>
        </a>

        <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.05]">
          Access any machine,<br />from anywhere.
        </h1>
        <p className="text-sm text-zinc-400 mt-4 leading-relaxed">
          Register your computers, watch them live, and take full remote control from your browser. Secure sessions, zero configuration.
        </p>

        <div className="mt-8 space-y-3">
          <div className="flex items-center gap-3 text-sm text-zinc-300">
            <Lightning size={18} weight="fill" className="text-blue-500" /> Live screen streaming
          </div>
          <div className="flex items-center gap-3 text-sm text-zinc-300">
            <Monitor size={18} weight="fill" className="text-blue-500" /> Full mouse & keyboard control
          </div>
          <div className="flex items-center gap-3 text-sm text-zinc-300">
            <ShieldCheck size={18} weight="fill" className="text-blue-500" /> Private, per-account machines
          </div>
        </div>

        <label className="flex items-start gap-2.5 mt-10 cursor-pointer select-none" data-testid="login-agree-label">
          <input
            type="checkbox"
            data-testid="login-agree-checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-zinc-600 bg-black accent-blue-600 cursor-pointer"
          />
          <span className="text-xs text-zinc-400 leading-relaxed">
            I agree to the{" "}
            <Link to="/terms" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">Terms of Service</Link>,{" "}
            <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">Privacy Policy</Link>, and confirm I am authorized to remotely access any computer I connect.
          </span>
        </label>

        <Button
          data-testid="google-login-button"
          onClick={handleLogin}
          disabled={!agreed}
          className="w-full mt-4 bg-white text-black hover:bg-zinc-200 font-medium h-12 rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <GoogleLogo size={20} weight="bold" className="mr-2" /> Continue with Google
        </Button>

        <div className="flex items-center gap-3 my-5">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-xs text-zinc-600 font-mono uppercase tracking-widest">or sign up with email</span>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        {sent ? (
          <div data-testid="magic-link-sent" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-center">
            <CheckCircle size={30} weight="fill" className="text-emerald-500 mx-auto mb-2" />
            <p className="text-sm text-zinc-200">Check <span className="font-semibold">{email}</span> for a confirmation link.</p>
            <p className="text-xs text-zinc-500 mt-1">It expires in 20 minutes. You can close this tab.</p>
          </div>
        ) : (
          <form onSubmit={handleMagicLink} className="space-y-3" data-testid="magic-link-form">
            <input
              data-testid="signup-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="w-full h-11 rounded-md bg-white/5 border border-white/10 px-3.5 text-sm text-white placeholder:text-zinc-500 focus:border-blue-500 focus:outline-none transition-colors"
            />
            <input
              data-testid="signup-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full h-11 rounded-md bg-white/5 border border-white/10 px-3.5 text-sm text-white placeholder:text-zinc-500 focus:border-blue-500 focus:outline-none transition-colors"
            />
            <select
              data-testid="signup-heard-select"
              value={heard}
              onChange={(e) => setHeard(e.target.value)}
              className={`w-full h-11 rounded-md bg-white/5 border border-white/10 px-3.5 text-sm focus:border-blue-500 focus:outline-none transition-colors ${heard ? "text-white" : "text-zinc-500"}`}
            >
              <option value="" disabled className="bg-zinc-900 text-zinc-500">How did you hear about us?</option>
              {HEARD_OPTIONS.map((o) => (
                <option key={o} value={o} className="bg-zinc-900 text-white">{o}</option>
              ))}
            </select>
            {err && <p data-testid="signup-error" className="text-xs text-red-400">{err}</p>}
            <Button
              type="submit"
              data-testid="magic-link-submit"
              disabled={!agreed || !name.trim() || !email.trim() || !heard || sending}
              className="w-full h-12 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {sending ? <CircleNotch size={18} className="animate-spin" /> : <><EnvelopeSimple size={18} weight="bold" className="mr-2" /> Email me a sign-up link</>}
            </Button>
            {!agreed && <p className="text-center text-xs text-zinc-600">Please accept the terms above to continue.</p>}
          </form>
        )}
        <p className="text-center text-xs text-zinc-600 mt-4 font-mono">Secured by Emergent Auth</p>
        <p className="text-center text-xs text-zinc-500 mt-3">
          Here to help a technician? <Link to="/support" data-testid="login-support-code-link" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">Enter a support code</Link>
        </p>
      </div>
    </div>
  );
}
